<template>
  <IntercomLayout>
    <template #topbar>
      <SevaTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="daily-stats-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('dailyStats.title') }}</h1>
          <p class="page-subtitle">{{ $t('dailyStats.subtitle') }}</p>
        </div>
        <IntercomButton variant="primary" @click="openAddModal">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ $t('dailyStats.addStat') }}</span>
        </IntercomButton>
      </div>

      <!-- Summary Cards -->
      <div class="summary-row">
        <IntercomCard class="summary-card">
          <div class="summary-label">{{ $t('dailyStats.totalMeals') }}</div>
          <div class="summary-value">{{ formatNumber(summary.totalMeals) }}</div>
          <div class="summary-period">{{ $t('dailyStats.thisMonth') }}</div>
        </IntercomCard>
        <IntercomCard class="summary-card">
          <div class="summary-label">{{ $t('dailyStats.totalExpense') }}</div>
          <div class="summary-value">{{ formatCompactCurrency(summary.totalExpense) }}</div>
          <div class="summary-period">{{ $t('dailyStats.thisMonth') }}</div>
        </IntercomCard>
        <IntercomCard class="summary-card">
          <div class="summary-label">{{ $t('dailyStats.avgCostPerMeal') }}</div>
          <div class="summary-value">{{ formatCompactCurrency(summary.avgCostPerMeal) }}</div>
          <div class="summary-period">{{ $t('dailyStats.thisMonth') }}</div>
        </IntercomCard>
      </div>

      <!-- Date Filter -->
      <div class="filters-row">
        <IntercomDateInput
          v-model="filterDateFrom"
          class="date-input"
          @update:model-value="fetchStats"
        />
        <span class="date-separator">to</span>
        <IntercomDateInput
          v-model="filterDateTo"
          class="date-input"
          @update:model-value="fetchStats"
        />
      </div>

      <!-- Stats Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="stats"
          :loading="isLoading"
          hoverable
          clickable
          @row-click="handleRowClick"
        >
          <template #cell-stat_date="{ value }">
            {{ formatDate(value) }}
          </template>
          <template #cell-meals_served="{ value }">
            <span class="meals-value">{{ formatNumber(value || 0) }}</span>
          </template>
          <template #cell-total_expense="{ value }">
            {{ formatCompactCurrency(value || 0) }}
          </template>
          <template #cell-cost_per_meal="{ value }">
            {{ formatCompactCurrency(value || 0) }}
          </template>
        </IntercomTable>
        <p v-if="!isLoading && stats.length === 0" class="empty-text">
          {{ $t('dailyStats.noStats') }}
        </p>
      </IntercomCard>

      <!-- Add/Edit Modal -->
      <IntercomModal v-model:show="showModal" :title="editingStat ? $t('dailyStats.editStat') : $t('dailyStats.addStat')">
        <DailyStatForm
          :stat="editingStat"
          :date="selectedDate"
          :loading="isSaving"
          @submit="handleSave"
          @cancel="closeModal"
        />
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useSeva } from '../composables/useSeva.js'
import { formatCompactCurrency } from '@shared/utils/currency.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomInput,
  IntercomDateInput,
  IntercomModal,
} from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import DailyStatForm from '../components/DailyStatForm.vue'
import { useNavigation } from '../composables/useNavigation.js'
import dayjs from 'dayjs'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { listDailyStats, upsertDailyStat } = useSeva()
const { navSections } = useNavigation()

// State
const stats = ref([])
const isLoading = ref(false)
const isSaving = ref(false)
const filterDateFrom = ref(dayjs().startOf('month').format('YYYY-MM-DD'))
const filterDateTo = ref(dayjs().format('YYYY-MM-DD'))
const showModal = ref(false)
const editingStat = ref(null)
const selectedDate = ref(dayjs().format('YYYY-MM-DD'))

const formatDate = (date) => date ? dayjs(date).format('DD MMM YYYY') : ''
const formatNumber = (num) => num ? num.toLocaleString('en-IN') : '0'

const columns = [
  { key: 'stat_date', title: t('dailyStats.date'), width: '140px' },
  { key: 'meals_served', title: t('dailyStats.mealsServed'), width: '140px', align: 'right' },
  { key: 'total_expense', title: t('dailyStats.totalExpense'), width: '140px', align: 'right' },
  { key: 'cost_per_meal', title: t('dailyStats.costPerMeal'), width: '140px', align: 'right' },
  { key: 'notes', title: t('dailyStats.notes'), width: '200px' },
]

const summary = computed(() => {
  const totalMeals = stats.value.reduce((sum, s) => sum + (s.meals_served || 0), 0)
  const totalExpense = stats.value.reduce((sum, s) => sum + (s.total_expense || 0), 0)
  const avgCostPerMeal = totalMeals > 0 ? totalExpense / totalMeals : 0
  return { totalMeals, totalExpense, avgCostPerMeal }
})

const fetchStats = async () => {
  isLoading.value = true
  try {
    const params = {}
    if (filterDateFrom.value) params.date_from = filterDateFrom.value
    if (filterDateTo.value) params.date_to = filterDateTo.value
    const response = await listDailyStats(params)
    stats.value = response?.stats || []
  } catch (error) {
    console.error('Failed to fetch stats:', error)
    toast.error(t('errors.loadFailed'))
  } finally {
    isLoading.value = false
  }
}

const openAddModal = () => {
  editingStat.value = null
  selectedDate.value = dayjs().format('YYYY-MM-DD')
  showModal.value = true
}

const handleRowClick = (stat) => {
  editingStat.value = stat
  showModal.value = true
}

const handleSave = async (formData) => {
  isSaving.value = true
  try {
    await upsertDailyStat(formData)
    toast.success(editingStat.value ? t('success.statUpdated') : t('success.statCreated'))
    closeModal()
    fetchStats()
  } catch (error) {
    console.error('Failed to save stat:', error)
    toast.error(t('errors.saveFailed'))
  } finally {
    isSaving.value = false
  }
}

const closeModal = () => {
  showModal.value = false
  editingStat.value = null
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(() => {
  fetchBusiness()
  fetchStats()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.daily-stats-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.summary-card {
  padding: var(--space-4);
  text-align: center;
}

.summary-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin-bottom: var(--space-1);
}

.summary-value {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
}

.summary-period {
  font-size: var(--font-size-xs);
  color: var(--color-gray-400);
  margin-top: var(--space-1);
}

.filters-row {
  display: flex;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
  align-items: center;
}

.date-input {
  width: 150px;
}

.date-separator {
  color: var(--color-gray-500);
}

.meals-value {
  font-weight: var(--font-weight-semibold);
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

@media (max-width: 768px) {
  .daily-stats-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .summary-row {
    grid-template-columns: 1fr;
  }

  .filters-row {
    flex-direction: column;
  }

  .date-input {
    width: 100%;
  }
}
</style>
